import { metresPerDegree } from "../metres";

// One equirectangular frame for field-scale distances. Latitude is 110,540 m
// per degree and longitude is 111,320 m per degree times cos(lat). Callers that
// only need a ratio can still project once and reuse the plane.

export function projectMetres(
  lng: number,
  lat: number,
  lat0: number,
): { x: number; y: number } {
  const { kx, ky } = metresPerDegree(lat0);
  return { x: lng * kx, y: lat * ky };
}

/* * Squared distance in an already-projected plane from p to segment a–b. */
export function segmentDistanceSq(
  px: number,
  py: number,
  ax: number,
  ay: number,
  bx: number,
  by: number,
): number {
  const vx = bx - ax;
  const vy = by - ay;
  const len2 = vx * vx + vy * vy;
  const t =
    len2 > 0
      ? Math.max(0, Math.min(1, ((px - ax) * vx + (py - ay) * vy) / len2))
      : 0;
  const dx = px - (ax + t * vx);
  const dy = py - (ay + t * vy);
  return dx * dx + dy * dy;
}

export function metresBetween(
  aLng: number,
  aLat: number,
  bLng: number,
  bLat: number,
  lat0: number,
): number {
  const { kx, ky } = metresPerDegree(lat0);
  return Math.hypot((aLng - bLng) * kx, (aLat - bLat) * ky);
}

/* * Metres from p to segment a–b, in the frame at lat0. */
export function segmentDistanceM(
  p: [number, number],
  a: [number, number],
  b: [number, number],
  lat0: number,
): number {
  const P = projectMetres(p[0], p[1], lat0);
  const A = projectMetres(a[0], a[1], lat0);
  const B = projectMetres(b[0], b[1], lat0);
  return Math.sqrt(segmentDistanceSq(P.x, P.y, A.x, A.y, B.x, B.y));
}
